import { useApolloClient } from '@apollo/client';
import {
  Divider,
  FileUploadField,
  IconName,
  ModalForm,
  type ModalComponentProps,
  type Nullable,
  type ObjectSchemaDefinition,
} from '@axinom/mosaic-ui';
import { Field } from 'formik';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  useImportServiceAccountsMutation,
  type ImportServiceAccountInput,
  type ImportServiceAccountsMutation,
} from '../../../../generated/id-service-env-administration';
import { ServiceAccountImportPreview } from './ServiceAccountImportPreview';
import {
  ConflictResolutions,
  type ImportFormData,
} from './ServiceAccountImportPreview.types';
import {
  parseServiceAccountsExportFile,
  toBackendConflictResolution,
} from './ServiceAccountsImport.utils';

type SubmitResponse = Nullable<
  ImportServiceAccountsMutation['importServiceAccounts']
>;

const importServiceAccountsValidationSchema =
  Yup.object<ObjectSchemaDefinition>({
    conflictResolution: Yup.string()
      .test(
        'conflict-resolution-required',
        'Choose how to resolve conflicts before proceeding.',
        function (this: Yup.TestContext) {
          return !this.parent.conflictState.hasUnresolvedConflicts;
        },
      )
      .test(
        'nothing-to-import',
        'No service accounts will be imported.',
        function (this: Yup.TestContext) {
          return !this.parent.conflictState.hasNothingToImport;
        },
      ),
  });

interface ServiceAccountImportModalProps extends ModalComponentProps {
  /** Called once the service accounts were imported successfully. */
  onImported: () => void;
}

export const ServiceAccountImportModal: React.FC<
  ServiceAccountImportModalProps
> = ({ closeModal, onImported }) => {
  const { envId } = useParams<{ envId: string }>();
  const apolloClient = useApolloClient();
  const [importServiceAccounts] = useImportServiceAccountsMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const saveData = useCallback(
    async (formData: ImportFormData): Promise<SubmitResponse> => {
      const { file, uploadStarted, uploadCompleted } =
        formData.serviceAccountsJson;

      uploadStarted();

      try {
        const parsed = await parseServiceAccountsExportFile(file);
        if (!parsed) {
          throw {
            title: 'Invalid file',
            body: 'Selected file is not a valid service accounts export.',
          };
        }

        const serviceAccounts: ImportServiceAccountInput[] =
          parsed.serviceAccounts.map((account) => ({
            name: account.name,
            permissions: account.permissions,
            isProtected: account.isProtected,
          }));

        const { data } = await importServiceAccounts({
          variables: {
            input: { version: parsed.version, serviceAccounts },
            environmentId: envId,
            conflictResolution: toBackendConflictResolution(
              formData.conflictResolution,
            ),
          },
        });

        const result = data?.importServiceAccounts;
        if (!result) {
          throw {
            title: 'Import failed',
            body: 'No response was received from the server.',
          };
        }

        if (result.errors.length > 0) {
          throw {
            title: 'Import validation failed',
            body: (
              <ul>
                {result.errors.map((error, index) => (
                  <li key={index}>
                    {error.entityName}: {error.errorCode}
                    {error.detail ? ` (${error.detail})` : ''}
                  </li>
                ))}
              </ul>
            ),
          };
        }

        onImported();

        return result;
      } finally {
        uploadCompleted();
      }
    },
    [importServiceAccounts, envId, onImported],
  );

  return (
    <ModalForm<ImportFormData, SubmitResponse>
      title="Import Service Accounts"
      closeModal={closeModal}
      saveHeaderActionConfig={{ label: 'Apply', icon: IconName.Checkmark }}
      validationSchema={importServiceAccountsValidationSchema}
      initialData={{
        loading: false,
        data: {
          conflictResolution: ConflictResolutions.NotSelected,
          conflictState: {
            hasUnresolvedConflicts: false,
            hasNothingToImport: false,
          },
        } as ImportFormData,
      }}
      saveData={saveData}
    >
      <Field
        name="serviceAccountsJson"
        label="Upload JSON"
        accept=".json"
        placeholder="Upload JSON to start validation"
        as={FileUploadField}
      />
      <Divider />
      <ServiceAccountImportPreview />
    </ModalForm>
  );
};
